import { useState } from 'react';
import { toast } from 'sonner';
import {
  ApiError,
  markPoolItemAsOpened,
  sellPoolItem,
  type ApiClient,
  type IframePoolOrder,
  type IframePoolOrderItem,
} from '../api';
import { gameEvents } from '../postMessage';
import { useWallet } from '../wallet';
import type { GlowPhase } from './RevealGlowOverlay';
import type { SellState } from './WinnerStep';

// DEV-MOCK — the backend doesn't yet ship a couple of post-reveal
// fields, so we layer them on locally: force `on_hands` status plus
// a fake `auto_sale_at` on the offer and `delivery_info` on the pool
// item. The badge (incl. grade / character) now comes from the real
// API and is NOT mocked here anymore. Lets us still exercise:
//   • the WinnerStep "Create Shipping" CTA,
//   • the ShippingModal countdown + fees breakdown,
// without waiting for the remaining backend changes. Flip to `false`
// to read everything straight from the API again.
const MOCK_REVEAL_BADGE = true;

/** Countdown target for the mocked `auto_sale_at`. Picks ~44 h
 *  ahead so the modal opens with a believable "43h ##m" countdown
 *  (matches Figma 703:28734). Computed lazily because the page may
 *  stay open across the day. */
function mockAutoSaleAt(): string {
  return new Date(Date.now() + 44 * 60 * 60 * 1000).toISOString();
}

const MOCK_DELIVERY_INFO: NonNullable<IframePoolOrderItem['deliveryInfo']> = {
  shippingFeeMin: 25,
  shippingFeeMax: 50,
  salesTax: 10.4,
  handlingFee: 10,
  totalMin: 35.9,
  totalMax: 70.9,
};

/** Layer the dev-only fields onto a real `mark_as_opened` response.
 *  Splitting the merge out keeps the call sites in `revealItem`
 *  one-liners and makes the mocked surface obvious from one place. */
function applyDevMocks(item: IframePoolOrderItem): IframePoolOrderItem {
  if (!MOCK_REVEAL_BADGE) return item;
  return {
    ...item,
    status: 'on_hands',
    bestActiveOffer: item.bestActiveOffer
      ? { ...item.bestActiveOffer, autoSaleAt: mockAutoSaleAt() }
      : item.bestActiveOffer,
    deliveryInfo: MOCK_DELIVERY_INFO,
  };
}

export type RevealStatus = 'ready' | 'winner';

export interface UseRevealMachineParams {
  client: ApiClient;
  orderId: string;
  item: IframePoolOrderItem;
  onRevealed: (updated: IframePoolOrderItem) => void;
  /** Re-load the order from the backend. Used to recover from a 422
   *  on `mark_as_opened` (item already opened — fresh payload has the
   *  authoritative `opened_at` + `best_active_offer`). */
  refetchOrder: () => Promise<IframePoolOrder | null>;
}

export interface RevealMachine {
  status: RevealStatus;
  /** Captured at mount: was the item ALREADY opened when this hook
   *  first initialised? Drives the winner screen's "instant" mode —
   *  a returning view (arrows / swipe / F5 into an opened pack) paints
   *  the final winner frame with NO entrance choreography, while a pack
   *  ripped live in this session (ready → reveal → winner) keeps the
   *  one-time animation. Frozen on first render, never reacts to later
   *  `openedAt` changes — that's intentional. */
  instant: boolean;
  current: IframePoolOrderItem;
  revealedBadge: string | null;
  glowPhase: GlowPhase | null;
  showRays: boolean;
  showBadgeSequence: boolean;
  sellState: SellState;
  onRevealStarted: () => Promise<void>;
  onExpansionStart: () => void;
  onGlowExpanded: () => void;
  onBadgeSequenceDone: () => void;
  onGlowFaded: () => void;
  onSell: (afterSold?: () => void) => Promise<void>;
}

/**
 * State machine for a single pack reveal. Owns the phase progression
 * (ready → glow reveal → glow expand → (optional badge sequence) →
 * winner), the buyback sell call, and the API call to
 * `mark_as_opened` including the 422-already-opened recovery path.
 *
 * The hook is intentionally stateful and self-contained: PackReveal
 * is a thin shell that wires the returned state into JSX and forwards
 * events from child overlays. Splitting this out keeps the component
 * close to a render function and lets the machine evolve independently.
 */
export function useRevealMachine({
  client,
  orderId,
  item,
  onRevealed,
  refetchOrder,
}: UseRevealMachineParams): RevealMachine {
  const [status, setStatus] = useState<RevealStatus>(
    item.openedAt ? 'winner' : 'ready',
  );
  // Frozen snapshot of "was already opened at mount". Lazy init so it
  // reflects the very first render only — a live rip starts `ready`
  // with `openedAt == null`, so `instant` stays false even after
  // `mark_as_opened` later fills `openedAt` in.
  const [instant] = useState(() => item.openedAt != null);
  const [current, setCurrent] = useState<IframePoolOrderItem>(item);
  const [sellState, setSellState] = useState<SellState>('idle');
  // `null` BOTH while we're waiting for `mark_as_opened` to come back AND
  // when the API has answered without a badge. Drives whether the glow
  // shows a rarity label / tint; the white plate now expands either way.
  const [revealedBadge, setRevealedBadge] = useState<string | null>(null);
  const [glowPhase, setGlowPhase] = useState<GlowPhase | null>(null);
  const [showBadgeSequence, setShowBadgeSequence] = useState(false);
  const [showRays, setShowRays] = useState(false);
  const wallet = useWallet();

  /**
   * Resolves the item's true post-open payload from the backend.
   *  - happy path: PUT `mark_as_opened` → merged item.
   *  - 422 ("already opened"): refetch the order and pick the
   *    fresh item that now carries `openedAt` + `best_active_offer`.
   *  - anything else: toast + null so the caller can decide whether
   *    to drop back to `ready` or stay where it is.
   */
  const revealItem = async (): Promise<IframePoolOrderItem | null> => {
    try {
      const updated = await markPoolItemAsOpened(client, item.id);
      const merged: IframePoolOrderItem = {
        ...current,
        ...updated,
        imageUrl: updated.imageUrl ?? current.imageUrl,
        title: updated.title || current.title,
      };
      return applyDevMocks(merged);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const refreshed = await refetchOrder();
        const fresh = refreshed?.poolItems.find(
          (i) => String(i.id) === String(item.id),
        );
        if (fresh?.openedAt) {
          return applyDevMocks({ ...current, ...fresh });
        }
      }
      const message =
        err instanceof Error ? err.message : 'Failed to open pack';
      toast.error(message);
      return null;
    }
  };

  /**
   * Fired the moment the slot wheel hands off to the `reveal` phase
   * inside ReadyStep (white glow appears). Dispatches the API call
   * and paints the rarity tint. The advance to WinnerStep happens
   * later, when the glow finishes its scale-up wipe.
   */
  const onRevealStarted = async () => {
    setShowRays(true);
    setGlowPhase('reveal');
    const next = await revealItem();
    if (!next) {
      // API failure: tear down the reveal scaffolding so the user
      // isn't stuck staring at infinite rays + plain white glow with
      // no CTA. Toast was already raised by `revealItem`.
      setShowRays(false);
      setGlowPhase(null);
      return;
    }
    setCurrent(next);
    setRevealedBadge(next.badge?.name ?? null);
    gameEvents.itemRevealed(
      orderId,
      String(item.id),
      next.title,
      next.fairMarketValue,
    );
    onRevealed(next);
  };

  /**
   * Fired by ReadyStep when the reveal hold is over. Always ramps the
   * glow overlay from `reveal` (steady plate) to `expand` (same plate
   * scales up to fill the screen) — regardless of whether there's a
   * badge. With no badge the plate just expands without a label inside
   * (`RevealGlowOverlay` only renders the label when `badge` is set),
   * and `onGlowExpanded` then drops straight to WinnerStep.
   */
  const onExpansionStart = () => {
    setGlowPhase('expand');
  };

  /** Glow overlay has finished its scale-up. Drop the rays + branch:
   *    • Badge text present → mount BadgeRevealSequence after a short
   *      silence. WinnerStep mount is DEFERRED until the sequence
   *      finishes.
   *    • No badge text → swap to winner now and start the plate
   *      fade-out so the WinnerStep enter animations sync with the
   *      pelt clearing.
   */
  const onGlowExpanded = () => {
    setShowRays(false);
    const b = current.badge;
    if (b && (b.character || b.grade)) {
      // Pure silence — label is already gone, this is the breath
      // between phases. Keep short; longer feels like a hang.
      window.setTimeout(() => setShowBadgeSequence(true), 180);
    } else {
      setStatus('winner');
      setGlowPhase('fade');
    }
  };

  /** Badge sequence finished. Mount WinnerStep AND start the plate
   *  fade in the same beat — the typewriter / rolling-number
   *  animations land in sync with the white pelt clearing instead
   *  of finishing invisibly behind it. */
  const onBadgeSequenceDone = () => {
    setShowBadgeSequence(false);
    setStatus('winner');
    setGlowPhase('fade');
  };

  /** Glow has finished its fade — unmount the overlay entirely. */
  const onGlowFaded = () => {
    setGlowPhase(null);
  };

  const onSell = async (afterSold?: () => void) => {
    const offerSnapshot = current.bestActiveOffer;
    if (!offerSnapshot || sellState !== 'idle') return;

    setSellState('selling');
    try {
      await sellPoolItem(client, orderId, current.id);
      setSellState('sold');
      gameEvents.itemSold(orderId, String(current.id), offerSnapshot.grossPrice);
      void wallet.refresh();
      // Optional post-sell callback. Sell-only path (plain "Accept
      // Offer") asks the host to close the reveal screen and drop
      // the user back on the pack detail page; the rip-again path
      // skips it because the caller is about to open a fresh purchase
      // flow instead.
      afterSold?.();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to sell item';
      toast.error(message);
      setSellState('idle');
    }
  };

  return {
    status,
    instant,
    current,
    revealedBadge,
    glowPhase,
    showRays,
    showBadgeSequence,
    sellState,
    onRevealStarted,
    onExpansionStart,
    onGlowExpanded,
    onBadgeSequenceDone,
    onGlowFaded,
    onSell,
  };
}
